<template>
  <div class="file-upload-page">
    <div class="upload-panel">
      <div class="upload-panel-header">
        <div class="upload-panel-heading">
          <h3 class="upload-panel-title">Upload settings</h3>
          <p class="upload-panel-subtitle">Choose the document type and add your files</p>
        </div>
        <div class="status-legend">
          <span class="status-legend-item">
            <span class="status-badge status-completed"><i class="las la-check"></i></span>
            <span>Completed</span>
          </span>
          <span class="status-legend-item">
            <span class="status-badge status-pending"><i class="las la-clock"></i></span>
            <span>Pending</span>
          </span>
          <span class="status-legend-item">
            <span class="status-badge status-processing"><i class="las la-sync"></i></span>
            <span>Processing</span>
          </span>
          <span class="status-legend-item">
            <span class="status-badge status-error"><i class="las la-times"></i></span>
            <span>Error</span>
          </span>
        </div>
      </div>

      <div class="upload-controls-row">
        <div v-if="!preSelectedFileType" class="file-type-selection">
          <label for="file-type" class="form-label">Document Type</label>
          <div class="custom-dropdown" :class="{ 'error': !selectedFileType && showFileTypeError }">
            <div
              class="dropdown-trigger"
              @click="filesUploaded ? null : toggleDropdown()"
              :class="{ 'open': isDropdownOpen, 'disabled': filesUploaded }"
            >
              <span class="dropdown-text">{{ selectedFileType ? getFileTypeLabel(selectedFileType) : 'Choose document type...' }}</span>
              <i class="las la-angle-down dropdown-arrow" :class="{ 'rotated': isDropdownOpen }"></i>
            </div>
            <div v-if="isDropdownOpen" class="dropdown-menu">
              <div
                v-for="option in fileTypeOptions"
                :key="option.value"
                class="dropdown-option"
                :class="{ 'selected': selectedFileType === option.value }"
                @click="selectFileType(option.value)"
              >
                {{ option.label }}
              </div>
            </div>
          </div>
          <div v-if="!selectedFileType && showFileTypeError" class="file-type-error">
            Please select a document type before uploading
          </div>
        </div>

        <div v-if="preSelectedFileType && selectedFileType" class="selected-file-type-display">
          <label class="form-label">Document Type</label>
          <div class="file-type-badge">
            <span>{{ getFileTypeLabel(selectedFileType) }}</span>
          </div>
        </div>

        <div v-if="showBatchMode" class="upload-mode-block">
          <label class="form-label">Upload Mode</label>
          <div class="upload-mode-toggle">
            <button
              type="button"
              class="mode-btn"
              :class="{ active: !batchMode }"
              @click="setUploadMode(false)"
            >
              Single
            </button>
            <button
              type="button"
              class="mode-btn"
              :class="{ active: batchMode }"
              @click="setUploadMode(true)"
            >
              Batch
            </button>
          </div>
        </div>
      </div>

      <ScanProgressBar
        :show-progress="scanProgress.show"
        :progress-percentage="scanProgress.percentage"
        :current-page="scanProgress.currentPage"
        :total-pages="scanProgress.totalPages"
        :task-type="scanProgress.taskType"
        :progress-message="scanProgress.message"
        :batch-files="scanProgress.batchFiles"
        :batch-stats="scanProgress.batchStats"
      />

      <div v-if="!batchMode && !selectedFile && selectedFileType" class="file-input-container" :class="{ 'disabled': !selectedFileType }">
        <input
          type="file"
          :accept="getFileAcceptType(selectedFileType)"
          @change="handleFileChange"
          ref="fileInput"
          class="file-input"
          :disabled="!selectedFileType"
          :style="{ pointerEvents: !selectedFileType ? 'none' : 'auto' }"
        />
        <div class="file-input-overlay" @click="selectedFileType ? $refs.fileInput.click() : null">
          <i class="las la-cloud-upload-alt upload-icon"></i>
          <p>Click to select a {{ getFileTypeDescription(selectedFileType) }} file</p>
          <p class="file-hint">or drag and drop here</p>
        </div>
      </div>

      <div v-if="batchMode && selectedFiles.length === 0 && showBatchMode" class="file-input-container" :class="{ 'disabled': !selectedFileType }">
        <input
          type="file"
          :accept="getFileAcceptType(selectedFileType)"
          multiple
          @change="handleBatchFileChange"
          ref="batchFileInput"
          class="file-input"
          :disabled="!selectedFileType"
          :style="{ pointerEvents: !selectedFileType ? 'none' : 'auto' }"
        />
        <div class="file-input-overlay" @click="selectedFileType ? $refs.batchFileInput.click() : null">
          <i class="las la-cloud-upload-alt upload-icon"></i>
          <p>{{ getBatchSelectHint() }}</p>
          <p class="file-hint">or drag and drop here</p>
        </div>
      </div>

      <div v-if="!selectedFileType" class="upload-placeholder">
        <i class="las la-folder-open"></i>
        <p>Select a document type to begin uploading</p>
      </div>

      <div v-if="!batchMode && selectedFile" class="document-card selected-file">
        <div class="document-card-content">
          <div class="document-icon-large" :class="getFileIconModifier(selectedFile)">
            <i :class="getFileIcon(selectedFile)"></i>
          </div>
          <span class="document-name">{{ selectedFile.name }}</span>
          <span class="document-meta">{{ formatFileSize(selectedFile.size) }}</span>
          <span v-if="checkingPassword" class="checking-password-indicator">
            <BaseSpinner size="small" />
            Checking password protection...
          </span>
          <span v-else-if="isPasswordProtected" class="password-protected-indicator">
            <i class="las la-lock"></i>
            Password Protected
          </span>
          <button
            type="button"
            class="change-file-btn"
            @click="changeFile"
            v-if="!isUploadInProgress && !checkingPassword"
          >
            <i class="las la-edit"></i>
            Change
          </button>
        </div>
      </div>

      <PasswordInput
        :show="showPasswordInput"
        :submitting="submittingPassword"
        :error="passwordError"
        input-id="pdf-password-input"
        @submit="submitPassword"
      />

      <div v-if="batchMode && selectedFiles.length > 0 && !isUploadInProgress" class="selected-files">
        <div class="files-header">
          <h3 class="subsection-title">Selected Files ({{ selectedFiles.length }})</h3>
          <div class="files-header-actions">
            <BaseButton
              v-if="showBatchMode"
              type="button"
              stylevariant="secondary"
              :cus_style="{ width: 'auto', padding: '0.35rem 0.65rem', fontSize: '0.8rem' }"
              class="add-more-btn"
              @click="addMoreFiles"
            >
              <i class="las la-plus"></i>
              Add More
            </BaseButton>
            <BaseButton
              type="button"
              stylevariant="secondary"
              :cus_style="{ width: 'auto', padding: '0.35rem 0.65rem', fontSize: '0.8rem' }"
              class="clear-files-btn"
              @click="clearFiles"
            >
              <i class="las la-times"></i>
              Clear All
            </BaseButton>
          </div>
        </div>
        <input
          type="file"
          :accept="getFileAcceptType(selectedFileType)"
          multiple
          @change="handleBatchFileChange"
          ref="batchFileInput"
          class="hidden-file-input"
          :disabled="!selectedFileType"
          :style="{ pointerEvents: !selectedFileType ? 'none' : 'auto' }"
        />
        <div class="files-list">
          <div
            v-for="(file, index) in selectedFiles"
            :key="`${file.name}-${index}`"
            class="document-card file-item"
          >
            <div class="document-card-content">
              <div class="document-icon-large" :class="getFileIconModifier(file)">
                <i :class="getFileIcon(file)"></i>
              </div>
              <span class="document-name">{{ file.name }}</span>
              <span class="document-meta">{{ formatFileSize(file.size) }}</span>
              <button
                type="button"
                class="remove-file-btn"
                @click="removeFile(index)"
                v-if="!isUploadInProgress"
                title="Remove file"
              >
                <i class="las la-trash"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="upload-actions">
        <BaseButton
          v-if="!batchMode && selectedFile && (!isPasswordProtected || passwordSubmitted)"
          type="button"
          stylevariant="primary"
          :disabled="isUploadInProgress || !selectedFileType || checkingPassword"
          :loading="isUploadInProgress"
          :cus_style="{ width: 'auto', padding: '0.625rem 1.25rem' }"
          class="upload-action-btn"
          @click="uploadFile"
        >
          <i v-if="!isUploadInProgress" class="las la-cloud-upload-alt"></i>
          <span v-if="isProcessingProgress">Processing…</span>
          <span v-else-if="uploading">Uploading…</span>
          <span v-else>Upload File</span>
        </BaseButton>
        <BaseButton
          v-if="batchMode && selectedFiles.length > 0 && showBatchMode"
          type="button"
          stylevariant="primary"
          :disabled="isUploadInProgress || !selectedFileType || checkingBatchPassword || (isBatchPasswordProtected && !passwordSubmitted)"
          :loading="isUploadInProgress"
          :cus_style="{ width: 'auto', padding: '0.625rem 1.25rem' }"
          class="upload-action-btn"
          @click="uploadBatchFiles"
        >
          <i v-if="!isUploadInProgress" class="las la-cloud-upload-alt"></i>
          <span v-if="isProcessingProgress">Processing…</span>
          <span v-else-if="uploading">Uploading {{ selectedFiles.length }} files…</span>
          <span v-else>Upload {{ selectedFiles.length }} Files</span>
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<script>
import ScanProgressBar from './ScanProgressBar.vue'
import BaseSpinner from '@/components/UI/BaseSpinner.vue'
import BaseButton from '@/components/UI/BaseButton.vue'
import PasswordInput from './PasswordInput.vue'
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';

// Configure worker for pdfjs-dist
// Using CDN for compatibility with version 2.16.105
pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js';

export default {
  name: 'FileUpload',
  components: {
    ScanProgressBar,
    BaseSpinner,
    BaseButton,
    PasswordInput
  },
  props: {
    scanProgress: {
      type: Object,
      default: () => ({
        show: false,
        percentage: 0,
        currentPage: 0,
        totalPages: 0,
        taskType: 'upload',
        batchFiles: [],
        batchStats: null
      })
    },
    preSelectedFile: {
      type: Object,
      default: null
    },
    preSelectedFileType: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      selectedFile: null,
      selectedFiles: [],
      uploading: false,
      batchMode: false,
      selectedFileType: this.preSelectedFileType || 'bank_statement',
      showFileTypeError: false,
      isDropdownOpen: false,
      filesUploaded: false, // Track if files have been uploaded
      // AECB Password properties
      filePassword: '',
      passwordSubmitted: false,
      submittingPassword: false,
      passwordError: null,
      isPasswordProtected: false,
      checkingPassword: false,
      // Batch password state
      isBatchPasswordProtected: false,
      checkingBatchPassword: false,
      fileTypeOptions: [
        { value: 'emirates_id', label: 'Emirates ID' },
        { value: 'trade_license', label: 'Trade License' },
        { value: 'vat_statement', label: 'VAT Statement' },
        { value: 'aecb_report', label: 'AECB Report' },
        { value: 'bank_statement', label: 'Bank Statement' },
        { value: 'multi_account_statement', label: 'Multi-Account Statement' },
        { value: 'payer_list', label: 'Payer List' },
        { value: 'vendor_list', label: 'Vendor List' },
        { value: 'credit_model', label: 'Credit Model' },
        { value: 'moa', label: 'Memorandum of Association' },
        { value: 'aoa', label: 'Articles of Association' },
        { value: 'audit_report', label: 'Audit Report' },
        { value: 'erp_extract', label: 'ERP Extract' }
      ]
    }
  },
  computed: {
    showPasswordInput() {
      //Single
      if (!this.batchMode && this.selectedFile && this.isPasswordProtected && !this.passwordSubmitted) {
        return true;
      }
      //Batch
      if (this.batchMode && this.selectedFiles.length > 0 && this.isBatchPasswordProtected && !this.passwordSubmitted) {
        return true;
      }
      return false;
    },
    showBatchMode() {
      return this.supportsBatchUpload(this.selectedFileType);
    },
    isProcessingProgress() {
      return !!(this.scanProgress && this.scanProgress.show);
    },
    isUploadInProgress() {
      return this.uploading || this.isProcessingProgress;
    }
  },
  watch: {
    'scanProgress.show'(show, wasShowing) {
      if (wasShowing && !show) {
        this.uploading = false;
        this.filesUploaded = false;
      }
    }
  },
  mounted() {
    if (this.preSelectedFile) {
      this.selectedFile = this.preSelectedFile;
    }
    if (this.preSelectedFileType) {
      this.selectedFileType = this.preSelectedFileType;
    }
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    goBack() {
      this.$emit('cancel');
    },

    supportsBatchUpload(fileType) {
      return ['bank_statement', 'multi_account_statement', 'vat_statement'].includes(fileType);
    },

    acceptsCsvInBatch(fileType) {
      return fileType === 'bank_statement' || fileType === 'multi_account_statement';
    },

    getBatchSelectHint() {
      if (this.acceptsCsvInBatch(this.selectedFileType)) {
        return 'Click to select multiple PDF or CSV files';
      }
      return 'Click to select multiple PDF files';
    },
    
    async checkPasswordProtected(file) {
      if (file.type !== 'application/pdf') {
        return false;
      }

      const reader = new FileReader();

      return new Promise((resolve) => {
        reader.onload = async () => {
          try {
            const typedArray = new Uint8Array(reader.result);
            await pdfjsLib.getDocument({ data: typedArray }).promise;
            // Successfully opened without password - not protected
            resolve(false); 
          } catch (error) {
            if (error?.name === "PasswordException") {
              // Check if it needs a password (NEED_PASSWORD = 1)
              const needPassword = pdfjsLib.PasswordResponses?.NEED_PASSWORD ?? 1;
              if (error.code === needPassword) {
                resolve(true); // Password required
              } else {
                resolve(false);
              }
            } else {
              resolve(false);
            }
          }
        };

        reader.onerror = () => {
          resolve(false); 
        };

        reader.readAsArrayBuffer(file);
      });
    },
    
    setUploadMode(isBatch) {
      this.batchMode = isBatch;
      if (isBatch) {
        this.selectedFile = null;
        this.$refs.fileInput && (this.$refs.fileInput.value = '');
      } else {
        this.selectedFiles = [];
        this.$refs.batchFileInput && (this.$refs.batchFileInput.value = '');
      }
    },
    
    async handleFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        const isValidFile = this.validateFileType(file, this.selectedFileType);
        if (isValidFile) {
          this.selectedFile = file;
          
          // Reset password state when a new file is selected
          this.isPasswordProtected = false;
          this.passwordSubmitted = false;
          this.filePassword = '';
          this.passwordError = null;
          
          if (file.type === 'application/pdf') {
            this.checkingPassword = true;
            try {
              this.isPasswordProtected = await this.checkPasswordProtected(file);
            } catch (error) {
              console.error('Error checking password protection:', error);
              this.isPasswordProtected = false;
            } finally {
              this.checkingPassword = false;
            }
          }
        } else {
          this.$refs.fileInput.value = '';
        }
      }
      this.uploading = false;
    },
    
    async handleBatchFileChange(event) {
      const files = Array.from(event.target.files);
      const validFiles = files.filter(file => this.validateFileType(file, this.selectedFileType));
      
      if (validFiles.length !== files.length) {
        const acceptedTypes = this.acceptsCsvInBatch(this.selectedFileType)
          ? 'PDF or CSV'
          : 'PDF';
        alert(`Some files were not ${acceptedTypes} files and were skipped.`);
      }
      
      if (validFiles.length > 0) {
        // Filter out duplicate files by name and size
        const newFiles = validFiles.filter(newFile => 
          !this.selectedFiles.some(existingFile => 
            existingFile.name === newFile.name && existingFile.size === newFile.size
          )
        );
        
        if (newFiles.length !== validFiles.length) {
          const duplicateCount = validFiles.length - newFiles.length;
          alert(`${duplicateCount} duplicate file(s) were skipped.`);
        }
        
        if (newFiles.length > 0) {
          this.selectedFiles = [...this.selectedFiles, ...newFiles];
          // Detect if any of the batch files require a password
          if (this.supportsBatchUpload(this.selectedFileType)) {
            this.checkingBatchPassword = true;
            try {
              this.isBatchPasswordProtected = await this.checkBatchPasswordProtected(this.selectedFiles);
              if (!this.isBatchPasswordProtected) {
                // Reset password state if not needed
                this.filePassword = '';
                this.passwordSubmitted = false;
                this.passwordError = null;
              }
            } catch (e) {
              console.error('Error checking batch password protection:', e);
              this.isBatchPasswordProtected = false;
            } finally {
              this.checkingBatchPassword = false;
            }
          }
        }
      }
      
      this.$refs.batchFileInput.value = '';
      this.uploading = false;
    },

    async checkBatchPasswordProtected(files) {
      const needPassword = pdfjsLib.PasswordResponses?.NEED_PASSWORD ?? 1;
      for (const file of files) {
        const requires = await this.checkPasswordProtected(file).catch(() => false);
        if (requires) return true;
      }
      return false;
    },
    
    uploadFile() {
      if (!this.selectedFileType) {
        this.showFileTypeError = true;
        return;
      }
      
      if (this.selectedFile) {
        // For password-protected files, emit the password as a third parameter
        if (this.isPasswordProtected && this.passwordSubmitted) {
          this.$emit('file-selected', this.selectedFile, this.selectedFileType, this.filePassword);
        } else {
          this.$emit('file-selected', this.selectedFile, this.selectedFileType);
        }
        this.filesUploaded = true;
      }
      this.uploading = true;
    },
    
    uploadBatchFiles() {
      if (!this.selectedFileType) {
        this.showFileTypeError = true;
        return;
      }
      
      if (this.selectedFiles.length > 0) {
        // Emit password as 3rd param if protected and submitted
        if (this.isBatchPasswordProtected && this.passwordSubmitted) {
          this.$emit('batch-files-selected', this.selectedFiles, this.selectedFileType, this.filePassword);
        } else {
          this.$emit('batch-files-selected', this.selectedFiles, this.selectedFileType);
        }
        this.filesUploaded = true;
      }
      this.uploading = true;
    },
    
    changeFile() {
      this.selectedFile = null;
      this.$refs.fileInput.value = '';
      this.$refs.fileInput.click();
      this.uploading = false;
      
      // Reset password state when changing files
      this.isPasswordProtected = false;
      this.filePassword = '';
      this.passwordSubmitted = false;
      this.submittingPassword = false;
      this.passwordError = null;
      this.checkingPassword = false;
    },
    
    removeFile(index) {
      this.selectedFiles.splice(index, 1);
    },
    
    clearFiles() {
      this.selectedFiles = [];
      this.$refs.batchFileInput.value = '';
      // Reset batch password state
      this.isBatchPasswordProtected = false;
      this.checkingBatchPassword = false;
      this.filePassword = '';
      this.passwordSubmitted = false;
      this.passwordError = null;
    },
    
    addMoreFiles() {
      if (this.selectedFileType) {
        this.$refs.batchFileInput.click();
      }
    },
    
    onFileTypeChange() {
      this.showFileTypeError = false;
    },
    
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen;
    },
    
    selectFileType(value) {
      this.selectedFileType = value;
      this.isDropdownOpen = false;
      this.showFileTypeError = false;
      
      // Reset password state when changing file type
      this.isPasswordProtected = false;
      this.filePassword = '';
      this.passwordSubmitted = false;
      this.submittingPassword = false;
      this.passwordError = null;
      this.checkingPassword = false;
      
      // Reset batch mode for file types that don't support batch upload
      if (!this.supportsBatchUpload(value)) {
        this.batchMode = false;
        this.selectedFiles = [];
        this.$refs.batchFileInput && (this.$refs.batchFileInput.value = '');
      }
      
    },
    
    getFileTypeLabel(value) {
      const option = this.fileTypeOptions.find(opt => opt.value === value);
      return option ? option.label : value;
    },
    
    getFileAcceptType(fileType) {
      switch (fileType) {
        case 'payer_list':
        case 'vendor_list':
          return '.xlsx,.xls,.pdf';
        case 'emirates_id':
          return 'image/*,.pdf';
        case 'credit_model':
        case 'erp_extract':
          return '.xlsx,.xls';
        case 'bank_statement':
        case 'multi_account_statement':
          return 'application/pdf,.csv,text/csv';
        default:
          return 'application/pdf';
      }
    },
    
    getFileTypeDescription(fileType) {
      switch (fileType) {
        case 'payer_list':
        case 'vendor_list':
          return 'Excel or PDF';
        case 'emirates_id':
          return 'image or PDF';
        case 'credit_model':
        case 'erp_extract':
          return 'Excel';
        case 'bank_statement':
        case 'multi_account_statement':
          return 'PDF or CSV';
        default:
          return 'PDF';
      }
    },
    
    validateFileType(file, fileType) {
      switch (fileType) {
        case 'payer_list':
        case 'vendor_list': {
          const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                         file.type === 'application/vnd.ms-excel';
          const isPdf = file.type === 'application/pdf';
          if (!isExcel && !isPdf) {
            alert('Please select an Excel file (.xlsx, .xls) or PDF file.');
            return false;
          }
          return true;
        }
        case 'emirates_id': {
          const isImage = file.type.startsWith('image/');
          const isPdfEmirates = file.type === 'application/pdf';
          if (!isImage && !isPdfEmirates) {
            alert('Please select an image file (JPG, PNG) or PDF file.');
            return false;
          }
          return true;
        }
        case 'credit_model':
        case 'erp_extract': {
          const isExcelForCredit = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                                  file.type === 'application/vnd.ms-excel';
          if (!isExcelForCredit) {
            alert('Please select an Excel file (.xlsx, .xls).');
            return false;
          }
          return true;
        }
        case 'bank_statement':
        case 'multi_account_statement': {
          const isPdf = file.type === 'application/pdf';
          const isCsv = file.type === 'text/csv' || file.type === 'application/csv' || file.name.toLowerCase().endsWith('.csv');
          if (!isPdf && !isCsv) {
            alert('Please select a PDF or CSV file.');
            return false;
          }
          return true;
        }
        default:
          if (file.type !== 'application/pdf') {
            alert('Please select a PDF file.');
            return false;
          }
          return true;
      }
    },
    
    closeDropdown() {
      this.isDropdownOpen = false;
    },

    handleClickOutside(event) {
      // Use a small delay to prevent immediate closing when opening
      setTimeout(() => {
        const dropdownContainer = this.$el.querySelector('.custom-dropdown');
        if (dropdownContainer && !dropdownContainer.contains(event.target)) {
          this.closeDropdown();
        }
      }, 10);
    },
    
    formatFileSize(bytes) {
      if (!bytes) return 'Unknown';
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
    },

    getFileIcon(file) {
      if (!file) return 'las la-file';

      const ext = (file.name || '').split('.').pop()?.toLowerCase();
      const mime = file.type || '';

      if (mime === 'application/pdf' || ext === 'pdf') {
        return 'las la-file-pdf';
      }

      if (
        [
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'text/csv',
          'application/csv'
        ].includes(mime)
        || ['xlsx', 'xls', 'csv'].includes(ext)
      ) {
        return 'las la-file-excel';
      }

      return 'las la-file';
    },

    getFileIconModifier(file) {
      if (!file) return '';

      const ext = (file.name || '').split('.').pop()?.toLowerCase();
      const mime = file.type || '';

      if (mime === 'application/pdf' || ext === 'pdf') {
        return 'document-icon-large--pdf';
      }

      if (
        [
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'text/csv',
          'application/csv'
        ].includes(mime)
        || ['xlsx', 'xls', 'csv'].includes(ext)
      ) {
        return 'document-icon-large--excel';
      }

      return '';
    },

    async verifyPassword(file, password) {
      if (!file || file.type !== 'application/pdf') {
        return { valid: false, wrongPassword: false };
      }

      const reader = new FileReader();

      return new Promise((resolve) => {
        reader.onload = async () => {
          try {
            const typedArray = new Uint8Array(reader.result);
            const loadingTask = pdfjsLib.getDocument({ 
              data: typedArray, 
              password: password || undefined
            });
            
            await loadingTask.promise;
            resolve({ valid: true, wrongPassword: false });
          } catch (error) {
            if (error?.name === "PasswordException") {
              // Check the specific password error code
              const needPassword = pdfjsLib.PasswordResponses?.NEED_PASSWORD ?? 1;
              const incorrectPassword = pdfjsLib.PasswordResponses?.INCORRECT_PASSWORD ?? 2;
              
              if (error.code === needPassword) {
                // Still needs password (shouldn't happen if password was provided)
                resolve({ valid: false, wrongPassword: false });
              } else if (error.code === incorrectPassword) {
                // Wrong password provided
                resolve({ valid: false, wrongPassword: true });
              } else {
                resolve({ valid: false, wrongPassword: false });
              }
            } else {
              console.error('Error verifying password:', error);
              resolve({ valid: false, wrongPassword: false });
            }
          }
        };

        reader.onerror = () => {
          resolve({ valid: false, wrongPassword: false });
        };

        reader.readAsArrayBuffer(file);
      });
    },

    // Password methods
    async submitPassword(password) {
      if (!password || !password.trim()) {
        this.passwordError = 'Please enter a password';
        return;
      }

      this.submittingPassword = true;
      this.passwordError = null;

      try {
        if (this.batchMode && this.selectedFiles.length > 0) {
          // Verify same password for all files in batch
          for (const file of this.selectedFiles) {
            const res = await this.verifyPassword(file, password.trim());
            if (!res.valid) {
              this.passwordError = res.wrongPassword
                ? 'Incorrect password for one or more files. Please try again.'
                : 'Failed to verify password. Please try again.';
              this.passwordSubmitted = false;
              this.submittingPassword = false;
              return;
            }
          }
          // All files verified
          this.filePassword = password.trim();
          this.passwordSubmitted = true;
          this.uploadBatchFiles();
        } else {
          // Single file verification
          const result = await this.verifyPassword(this.selectedFile, password.trim());
          if (result.valid) {
            this.filePassword = password.trim();
            this.passwordSubmitted = true;
            this.uploadFile();
          } else if (result.wrongPassword) {
            this.passwordError = 'Incorrect password. Please try again.';
            this.passwordSubmitted = false;
          } else {
            this.passwordError = 'Failed to verify password. Please try again.';
            this.passwordSubmitted = false;
          }
        }
      } catch (error) {
        console.error('Password verification error:', error);
        this.passwordError = 'Failed to verify password. Please try again.';
        this.passwordSubmitted = false;
      } finally {
        this.submittingPassword = false;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../index.scss';

.file-upload-page {
  width: 100%;
}

.upload-panel {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.35rem 1.5rem 1.5rem;
  background: #fbfcfe;
  border: 1px solid #eef1f6;
  border-radius: 16px;
}

.upload-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
}

.upload-panel-heading {
  min-width: 0;
}

.upload-panel-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #101828;
  letter-spacing: -0.02em;
  font-family: $brand-font-bold;
}

.upload-panel-subtitle {
  margin: 0.25rem 0 0;
  font-size: 0.875rem;
  color: #667085;
  font-family: $brand-font-regular;
}

.status-legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.85rem;

  .status-legend-item {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.78rem;
    color: #4a5568;
    font-family: $brand-font-regular;

    .status-badge {
      width: 20px;
      height: 20px;
      display: inline-flex;
      align-items: center;
      justify-content: center;

      i { font-size: 0.95rem; }

      &.status-completed { color: #16a34a; i { color: #16a34a; } }
      &.status-pending { color: #6b7280; i { color: #6b7280; } }
      &.status-processing { color: #3b82f6; i { color: #3b82f6; } }
      &.status-error { color: #dc2626; i { color: #dc2626; } }
    }
  }
}

.upload-controls-row {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem 1.25rem;
  align-items: end;
}

.file-type-selection,
.selected-file-type-display {
  flex: 0 1 320px;
  width: 100%;
  max-width: 360px;
}

.form-label {
  display: block;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.045em;
  color: #667085;
  font-family: $brand-font-semi-bold;
  margin-bottom: 0.45rem;
}

.upload-mode-block {
  flex: 0 0 180px;
  width: 180px;
}

.custom-dropdown {
  position: relative;
  width: 100%;

  &.error .dropdown-trigger {
    border-color: #e53e3e;
    box-shadow: 0 0 0 3px rgba(#e53e3e, 0.12);
  }

  .dropdown-trigger {
    width: 100%;
    min-height: 42px;
    padding: 0.625rem 2.5rem 0.625rem 0.875rem;
    border: 1px solid #d3d9e4;
    border-radius: 8px;
    background: #ffffff;
    color: #121926;
    font-size: 0.95rem;
    font-weight: 500;
    font-family: $brand-font-regular;
    display: flex;
    align-items: center;
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
    box-sizing: border-box;

    &:hover:not(.disabled) {
      border-color: #c5cdd9;
    }

    &.open,
    &:focus {
      outline: none;
      border-color: #0f9d58;
      box-shadow: 0 0 0 3px rgba(15, 157, 88, 0.16);
    }

    &.disabled {
      cursor: not-allowed;
      opacity: 0.65;
      background: #f8fafc;
    }

    .dropdown-text {
      flex: 1;
      text-align: left;
      color: #121926;
    }

    .dropdown-arrow {
      position: absolute;
      right: 0.875rem;
      color: #98a2b3;
      transition: transform 0.2s ease;

      &.rotated {
        transform: rotate(180deg);
      }
    }
  }

  .dropdown-menu {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    background: #ffffff;
    border: 1px solid #e6e9f0;
    border-radius: 10px;
    box-shadow: 0 8px 24px -12px rgba(16, 24, 40, 0.28);
    z-index: 20;
    max-height: 260px;
    overflow-y: auto;

    .dropdown-option {
      padding: 0.7rem 0.875rem;
      cursor: pointer;
      font-size: 0.9rem;
      color: #121926;
      font-family: $brand-font-regular;

      &:hover {
        background: #f6fdf9;
      }

      &.selected {
        background: rgba($color-primary, 0.12);
        color: darken($color-primary, 28%);
        font-weight: 600;
      }
    }
  }
}

.file-type-error {
  margin-top: 0.45rem;
  color: #e53e3e;
  font-size: 0.8rem;
  font-family: $brand-font-regular;
}

.file-type-badge {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 42px;
  padding: 0.625rem 0.875rem;
  background: #ffffff;
  border: 1px solid #d3d9e4;
  border-radius: 8px;
  box-sizing: border-box;
  color: #121926;
  font-size: 0.95rem;
  font-weight: 500;
  font-family: $brand-font-medium;
  line-height: 1.3;
}

.upload-mode-toggle {
  display: inline-flex;
  width: 100%;
  gap: 0.35rem;
  background: #ffffff;
  border: 1px solid #e6e9f0;
  border-radius: 10px;
  padding: 0.25rem;

  .mode-btn {
    flex: 1;
    min-height: 34px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: #667085;
    font-size: 0.85rem;
    font-weight: 500;
    font-family: $brand-font-medium;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover:not(.active) {
      color: #101828;
      background: #f8fafc;
    }

    &.active {
      background: $color-primary;
      color: $color-secondary;
    }
  }
}

.file-input-container,
.upload-placeholder {
  width: 100%;
  height: 220px;
  border: 1.5px dashed #d3d9e4;
  border-radius: 14px;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.file-input-container {
  position: relative;
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
  cursor: pointer;

  &:hover:not(.disabled) {
    border-color: #0f9d58;
    background: #f6fdf9;
    box-shadow: 0 0 0 3px rgba(15, 157, 88, 0.08);
  }

  &.disabled {
    cursor: not-allowed;
    opacity: 0.65;
    background: #f8fafc;
    pointer-events: none;
  }
}

.file-input {
  position: absolute;
  opacity: 0;
  inset: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
}

.file-input-overlay {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  pointer-events: none;
  padding: 1.5rem;
  color: #344054;

  p {
    margin: 0;
    font-size: 0.95rem;
    font-family: $brand-font-medium;
  }
}

.upload-icon {
  font-size: 2rem;
  color: $color-primary;
  margin-bottom: 0.65rem;
}

.file-hint {
  margin-top: 0.35rem !important;
  font-size: 0.85rem !important;
  color: #98a2b3 !important;
  font-family: $brand-font-regular !important;
  font-weight: 400 !important;
}

.upload-placeholder {
  flex-direction: column;
  gap: 0.65rem;
  background: #ffffff;
  color: #98a2b3;
  text-align: center;
  padding: 1.5rem;

  i {
    font-size: 2rem;
    color: #cbd5e0;
  }

  p {
    margin: 0;
    font-size: 0.95rem;
    font-family: $brand-font-regular;
  }
}

.document-card {
  background: #ffffff;
  border: 1px solid #e6e9f0;
  border-radius: 12px;
  padding: 0.75rem 1rem;
}

.document-card-content {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  flex-wrap: wrap;
}

.document-icon-large {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  i {
    font-size: 1.25rem;
    color: $color-primary;
  }

  &--pdf i {
    color: #dc2626;
  }

  &--excel i {
    color: #028a0f;
  }
}

.document-name {
  flex: 1;
  min-width: 140px;
  font-size: 0.95rem;
  font-weight: 500;
  color: #101828;
  font-family: $brand-font-medium;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.document-meta {
  font-size: 0.85rem;
  color: #667085;
  font-family: $brand-font-regular;
  white-space: nowrap;
}

.checking-password-indicator,
.password-protected-indicator {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.85rem;
  font-family: $brand-font-regular;
}

.checking-password-indicator { color: #667085; }
.password-protected-indicator {
  color: #b45309;
  font-weight: 500;
  font-family: $brand-font-medium;
}

.change-file-btn,
.remove-file-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: $brand-font-medium;
}

.change-file-btn {
  background: rgba($color-primary, 0.1);
  color: darken($color-primary, 20%);
  border: 1px solid rgba($color-primary, 0.35);
  padding: 0.4rem 0.75rem;
  font-size: 0.85rem;

  &:hover {
    background: rgba($color-primary, 0.16);
  }
}

.selected-files {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.files-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.subsection-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #101828;
  letter-spacing: -0.01em;
  display: flex;
  align-items: center;
  gap: 0.55rem;
  font-family: $brand-font-bold;

  &::before {
    content: "";
    display: inline-block;
    width: 4px;
    height: 1rem;
    border-radius: 999px;
    background: linear-gradient(180deg, #0f9d58, #16a34a);
    flex-shrink: 0;
  }
}

.files-header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.add-more-btn,
.clear-files-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
}

.hidden-file-input {
  display: none;
}

.files-list {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  max-height: 260px;
  overflow-y: auto;
}

.file-item {
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: rgba($color-primary, 0.45);
    box-shadow: 0 2px 8px -4px rgba(16, 24, 40, 0.14);
  }
}

.remove-file-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  flex-shrink: 0;
  padding: 0;
  cursor: pointer;

  i {
    font-size: 1.35rem;
    color: #98a2b3;
    transition: color 0.15s ease;
  }

  &:hover i,
  &:focus i {
    color: #e53e3e;
  }
}

.upload-actions {
  display: flex;
  justify-content: flex-end;
}

.upload-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

@media (max-width: 768px) {
  .upload-panel {
    padding: 1.15rem;
  }

  .upload-controls-row {
    flex-direction: column;
    align-items: stretch;
  }

  .file-type-selection,
  .selected-file-type-display {
    max-width: none;
    flex: 1 1 auto;
  }

  .upload-mode-block {
    width: 100%;
    flex: 1 1 auto;
  }

  .file-input-container,
  .upload-placeholder {
    height: 200px;
  }

  .upload-actions {
    justify-content: stretch;

    .upload-action-btn {
      width: 100% !important;
    }
  }
}
</style>
